<script setup lang="ts">
/**
 * Wedding ring step widget (used when the wedding ring configuration - itemV2VariantsConf - is active).
 *
 * Unlike stepWidget.vue, this widget has no overview picture on the left. Instead it always shows
 * one step for the set-view plus one step for every item that is in the session.
 *
 * Clicking the set-view step does not navigate away. Instead it stays on the current view and sets
 * the app-wide "configureAll" flag (session/configureAll), which components (e.g. the variants list)
 * can use to show the set price instead of the single item price. Clicking an item step selects that
 * item and clears "configureAll" so only that item's price and variants are shown.
 *
 * If the session only contains a single item, an additional "add item" step is rendered. Clicking
 * it emits "add-item" with the next available item index (session.items.length), so the parent
 * component can call the backend to add a new item to the session.
 */
import {computed, ref} from "vue";
import {useStore} from "vuex";

const store = useStore();

const props = defineProps({
  session: {
    type: Object,
    required: true
  },
  itemIdx: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['delete-item', 'add-item']);

const configureAll = computed(() => store.getters["session/getConfigureAll"]);

const setPrice = computed(() => {
  let total = 0;
  for (const item of props.session.items || []) {
    total += item.price?.total || 0;
  }
  return total;
});

const selectSetView = () => {
  store.commit("session/setConfigureAll", true);
}

const selectItem = (idx: number) => {
  store.commit("session/setConfigureAll", false);
  store.commit("session/setItemIdx", idx);
}

const getItemName = (idx: number) => {
  const persons = props.session.persons || [];
  const person = persons.find((p: any) => p.items?.includes(idx));
  return person?.name || '';
};

const getItemSize = (item: any) => {
  const variants = item.variants || {};
  for (const variantIdx in variants) {
    const variantData = variants[variantIdx];
    if (variantData?.idx === 'size') {
      return variantData.text
    }
  }
  return '';
};

const showDeleteModal = ref(false);
const itemIdxToDelete = ref<number | null>(null);

const confirmDelete = (idx: number) => {
  itemIdxToDelete.value = idx;
  showDeleteModal.value = true;
};

const onConfirmDelete = () => {
  if (itemIdxToDelete.value !== null) {
    emit('delete-item', itemIdxToDelete.value);
    showDeleteModal.value = false;
    itemIdxToDelete.value = null;
  }
};

const addItem = () => {
  emit('add-item', props.session.items.length);
};

const setImage = computed(() => {
  let setImage = ""

  if (props.session.items.length == 1) {
    setImage = props.session.items[0].image.filename
  } else {

    for (let i = 0; i < props.session.items.length; i++) {
      if (setImage == "") {
        setImage = props.session.items[i].image.filename.replace("item/image/", "item/image/set/")
      } else {
        const filenameParts = props.session.items[i].image.filename.split("/")
        const itemHash = filenameParts[filenameParts.length - 1]

        setImage += `/${itemHash}`
      }
    }
  }

  return setImage
});

</script>

<template>
  <div class="w-full bg-white border-b border-gray-100 shadow-sm sticky top-0 z-50">
    <div class="max-w-screen-4xl mx-auto flex items-stretch overflow-hidden h-16 lg:h-16">

      <!-- Steps Section - one step for the set-view and one step for each item, always visible for wedding ring sessions -->
      <div class="flex-1 flex items-stretch bg-gray-100 gap-px overflow-x-auto no-scrollbar">

        <!-- Set-view step -->
        <div
            class="flex-1 flex items-center justify-center px-2 lg:px-6 relative cursor-pointer group transition-all duration-300 step-container min-w-[140px] lg:min-w-[200px]"
            :class="{'active': configureAll, 'completed': configureAll}"
            @click="selectSetView"
        >
          <div class="flex w-full justify-between items-center space-x-2 lg:space-x-4 pl-4">
            <div
                class="w-7 h-7 lg:w-12 lg:h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 shrink-0"
                :class="[configureAll ? 'border-white bg-white text-black' : 'border-gray-100 text-gray-300 group-hover:border-gray-200']"
            >
              <img :src="setImage" class="w-full h-full object-cover" alt=""/>
            </div>
            <div class="flex flex-col min-w-0 grow">
              <span
                  class="text-[9px] lg:text-[11px] font-bold uppercase tracking-wider truncate transition-colors"
                  :class="configureAll ? 'text-black font-bold' : 'text-gray-400 group-hover:text-gray-600'"
              >
                {{ $t('components.itemV2.itemV2Main.set_view') }}
              </span>
              <span
                  class="text-[8px] lg:text-[10px] font-medium truncate"
                  :class="configureAll ? 'text-black' : 'text-brand-main'"
              >
                {{ $n(setPrice, 'currency') }}
              </span>
            </div>
          </div>
        </div>

        <!-- One step per item -->
        <div
            v-for="(item, idx) in session.items"
            :key="item.item_number || idx"
            class="flex-1 flex items-center justify-center px-2 lg:px-6 relative cursor-pointer group transition-all duration-300 step-container min-w-[140px] lg:min-w-[200px]"
            :class="{'active': !configureAll && itemIdx == idx, 'completed': !configureAll && itemIdx == idx}"
            @click="selectItem(idx)"
        >
          <div class="flex w-full justify-between items-center space-x-2 lg:space-x-4 pl-4">
            <div
                class="w-7 h-7 lg:w-12 lg:h-12 rounded-full bg-white overflow-hidden border-2 transition-all duration-300 shrink-0 relative"
                :class="[!configureAll && itemIdx == idx ? 'border-white' : 'border-gray-100 group-hover:border-gray-200']"
            >
              <img v-if="item.image?.filename_layer" :src="item.image.filename_layer"
                   class="absolute insert-0 w-auto h-full aspect-square object-cover"
                   alt="Setting image"/>

              <img :src="item.image?.filename" class="w-full h-full object-cover" alt=""/>
            </div>
            <div class="flex flex-col min-w-0 grow">
              <span
                  class="text-[9px] lg:text-[11px] font-bold uppercase tracking-wider truncate transition-colors"
                  :class="!configureAll && itemIdx == idx ? 'text-black font-bold' : 'text-gray-400 group-hover:text-gray-600'"
              >
                {{ getItemName(parseInt(idx.toString())) || $t('components.stepWidget.setting.ring') }}
                <template v-if="!getItemName(parseInt(idx.toString())) && session.items.length > 1"> {{
                    idx + 1
                  }}</template>
                <template v-if="getItemSize(item)"> ({{ getItemSize(item) }})</template>
              </span>
              <span
                  class="text-[8px] lg:text-[10px] font-medium truncate"
                  :class="!configureAll && itemIdx == idx ? 'text-black' : 'text-brand-main'"
              >
                {{ $n(item.price?.total || 0, 'currency') }}
              </span>
            </div>
            <button
                @click.stop="confirmDelete(idx)"
                :title="$t('components.itemV2.itemV2ItemWidget.remove')"
                class="shrink-0 p-1 text-gray-500 hover:text-brand-main transition-colors cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                   stroke="currentColor" class="w-4 h-4 lg:w-5 lg:h-5">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Placeholder step to add a new item, only shown when there is a single item in the session -->
        <div
            v-if="session.items.length === 1"
            class="flex-1 flex items-center justify-center px-2 lg:px-6 relative cursor-pointer group transition-all duration-300 step-container min-w-[140px] lg:min-w-[200px]"
            @click="addItem"
        >
          <div class="flex w-full items-center space-x-2 lg:space-x-4 pl-4">
            <div
                class="w-7 h-7 lg:w-12 lg:h-12 rounded-full flex items-center justify-center border-2 border-dashed border-gray-200 text-gray-300 shrink-0 group-hover:border-gray-300 group-hover:text-gray-400 transition-all duration-300"
            >
              <span class="text-xs lg:text-sm font-bold">+</span>
            </div>
            <div class="flex flex-col min-w-0 grow">
              <span
                  class="text-[9px] lg:text-[11px] font-bold uppercase tracking-wider truncate text-gray-400 group-hover:text-gray-600 transition-colors">
                {{ $t('components.itemV2.itemV2Main.add_item') }}
              </span>
            </div>
          </div>
        </div>

      </div>

    </div>

    <transition name="fade">
      <div v-if="showDeleteModal"
           class="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm px-4"
           @click="showDeleteModal = false">
        <div class="bg-white p-8 max-w-sm w-full shadow-2xl border border-gray-100" @click.stop>
          <h3 class="text-xl font-ivymode mb-4 text-gray-900">
            {{ $t('components.itemV2.itemV2ItemWidget.remove_confirm_title') }}</h3>
          <p class="text-gray-600 mb-8 font-light italic">{{
              $t('components.itemV2.itemV2ItemWidget.remove_confirm_text')
            }}</p>
          <div class="space-y-3">
            <button
                @click="onConfirmDelete"
                class="block w-full text-white bg-brand-main p-2 text-center hover:bg-white hover:text-brand-main border border-brand-main text-sm font-light transition-colors"
            >
              {{ $t('components.itemV2.itemV2ItemWidget.confirm') }}
            </button>
            <button
                @click="showDeleteModal = false"
                class="block w-full text-brand-main bg-white p-2 text-center hover:bg-brand-gray border border-brand-main text-sm font-light transition-colors"
            >
              {{ $t('components.itemV2.itemV2ItemWidget.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.step-container {
  background-color: white;
  transition: background-color 0.3s;
  clip-path: polygon(0% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 0% 100%, 15px 50%);
  margin-left: -15px;
}

.step-container.active {
  background-color: #eaded5;
  z-index: 10;
}

.step-container:first-child {
  margin-left: 0;
  clip-path: polygon(0% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 0% 100%);
}

.step-container:last-child {
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 15px 50%);
}

.step-container:hover {
  background-color: #f9f9f9;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
